import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import React, { useState } from 'react';
import { 
  Card, 
  Input, 
  Button, 
  Typography, 
  Space, 
  List, 
  Avatar, 
  Dropdown, 
  Menu, 
  Empty, 
  Spin, 
  Alert,
  Tag,
  Modal,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PrinterOutlined, 
  MoreOutlined,
  FileTextOutlined,
  CalendarOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useTemplates } from '../../hooks/templateHooks';

const { Title, Text } = Typography;
const { Search } = Input;

const TemplateList = ({ onSelectTemplate, onCreateNew }) => {
  const { templates, loading, error, deleteTemplate, fetchTemplates } = useTemplates();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Handle search input change with debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setSearchLoading(true);
    
    // Debounce search requests
    const timeoutId = setTimeout(() => {
      fetchTemplates({ search: value })
        .finally(() => setSearchLoading(false));
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };
  
  // Handle template deletion
  const handleDelete = (id, name) => {
    Modal.confirm({
      title: 'Delete Template',
      content: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteTemplate(id);
        } catch (err) {
          Modal.error({
            title: 'Delete Failed',
            content: `Error deleting template: ${err.message}`
          });
        }
      }
    });
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate time ago string
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString);
    }
  };

  // Action menu for each template
  const getActionMenu = (template) => (
    <Menu>
      <Menu.Item 
        key="edit" 
        disabled={!currentCan('templates', 'update')}
        icon={<EditOutlined />} 
        onClick={() => onSelectTemplate(template.id, 'edit')}
      >
        Edit
      </Menu.Item>
      <Menu.Item 
        key="print" 
        disabled={!currentCan('templates', 'print')}
        icon={<PrinterOutlined />} 
        onClick={() => onSelectTemplate(template.id, 'preview')}
      >
        Print
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="delete" 
        disabled={!currentCan('templates', 'delete')}
        danger 
        icon={<DeleteOutlined />} 
        onClick={() => handleDelete(template.id, template.name)}
      >
        Delete
      </Menu.Item>
    </Menu>
  );

  if (error) {
    return (
      <Alert 
        message="Error Loading Templates" 
        description={error} 
        type="error" 
        showIcon 
        action={
          <Button size="small" onClick={() => fetchTemplates()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={3} style={{ margin: 0 }}>Templates</Title>
        <PermissionButton permissionModule="templates" permissionAction="create"
          type="primary" 
          icon={<PlusOutlined />}
          onClick={onCreateNew}
        >
          Create New Template
        </PermissionButton>
      </div>
      
      <Search
        placeholder="Search templates..."
        allowClear
        enterButton={<><SearchOutlined /> Search</>}
        size="large"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onSearch={handleSearch}
        loading={searchLoading}
        style={{ marginBottom: '24px' }}
      />
      
      <Spin spinning={loading && !searchLoading}>
        {templates.length === 0 ? (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              searchTerm 
                ? "No templates match your search criteria." 
                : "No templates yet. Create your first template to get started!"
            }
          >
            {!searchTerm && (
              <PermissionButton permissionModule="templates" permissionAction="create"
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={onCreateNew}
              >
                Create Now
              </PermissionButton>
            )}
          </Empty>
        ) : (
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 1,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 4,
            }}
            dataSource={templates}
            renderItem={template => (
              <List.Item>
                <Card
                  hoverable
                  actions={[
                    <Tooltip title="Edit Template" key="edit">
                      <PermissionButton permissionModule="templates" permissionAction="update"
                        type="text" 
                        icon={<EditOutlined />} 
                        onClick={() => onSelectTemplate(template.id, 'edit')}
                      />
                    </Tooltip>,
                    <Tooltip title="Print Template" key="print">
                      <PermissionButton permissionModule="templates" permissionAction="print"
                        type="text" 
                        icon={<PrinterOutlined />} 
                        onClick={() => onSelectTemplate(template.id, 'preview')}
                      />
                    </Tooltip>,
                    <Dropdown key="more" overlay={getActionMenu(template)} trigger={['click']}>
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>,
                  ]}
                >
                  <div onClick={() => onSelectTemplate(template.id, 'edit')}>
                    <Card.Meta
                      avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                      title={template.name}
                      description={
                        template.description ? (
                          <Text ellipsis={{ rows: 2 }} style={{ height: '36px' }}>
                            {template.description}
                          </Text>
                        ) : (
                          <Text type="secondary" italic>No description</Text>
                        )
                      }
                    />
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <CalendarOutlined style={{ marginRight: '4px' }} />
                        {getTimeAgo(template.updatedAt)}
                      </Text>
                      
                      <Tag color="blue">
                        {template.data?.length || 0} Elements
                      </Tag>
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Card>
  );
};

export default TemplateList;
